import type { App } from "@teamworksdev/react";

export const DocumentType = {
  org: "org",
  team: "team",
  profile: "profile",
  user_type: "user_type",
  other: "other",
} as const;
export type DocumentType = (typeof DocumentType)[keyof typeof DocumentType];

export const DocumentStatus = {
  active: "active",
  archive: "archive",
  inactive: "inactive",
} as const;
export type DocumentStatus = (typeof DocumentStatus)[keyof typeof DocumentStatus];

export type DocumentOwner = "org" | "team";
export type DocumentAttribute = string;
export type DocumentAttributeSrc = `${App}.${DocumentOwner}.${DocumentAttribute}`;

export type GetDocumentRequest = Record<
  string,
  {
    src: DocumentAttributeSrc[];
    default?: string;
    raise_on_missing?: boolean;
  }
>;

export type GetDocumentsRequest = {
  active?: boolean;
  active_for_current_app?: boolean;
  active_for_app?: App;
  apps?: App[];
  having_scopes?: string[];
  order_by?: string[];
  limit?: number;
  offset?: number;
  // Less performant, but allows the user to search using a keyword and you can select the fields it will search by
  keyword_search?: {
    keyword: string;
    fields: string[];
  };
  // More performant, but requires an exact match, and you can only search based a single field
  simple_search?: Record<string, string>;
  open_search?: object;
  search?: string;
  by_altkeys?: Record<string, string[]>;
  by_document_ids?: string[];
  view_rule: GetDocumentRequest;
  org_id?: string;
  document_type?: DocumentType;
  hasdata_for_oneof_app?: App[];
  // Exact-match variant: only documents whose app set is exactly these apps (plus axle)
  hasdata_for_only_apps?: App[];
};

type withSuffix<TKey, TSuffix extends string> = TKey extends string ? `${TKey}.${TSuffix}` : never;

export type ConstrainedDocumentScope<T, J extends App, K extends DocumentType> = Record<withSuffix<J, K>, T>;

export type DocumentScope<T, K extends DocumentType> = Partial<Record<withSuffix<App, K>, T>>;

export type DocumentData<T, K extends DocumentType> = {
  scopes: DocumentScope<T, K>;
  status: DocumentStatus;
  version_id: number;
};
export type DocumentDescriptor<T, K extends DocumentType> = {
  id: string;
  data: Record<App, DocumentData<T, K>>;
  document_type?: DocumentType;
  status: DocumentStatus;
};

export type UpsertDocumentResponse = {
  action: "created" | "updated";
  document_id: string;
  match_keys: Record<`${App}.${DocumentOwner}@${string}`, string>;
};
